import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../../src/api/client';
import { useToast } from '../../../src/components/Toast';
import Input from '../../../src/components/Input';
import Button from '../../../src/components/Button';
import InlineConfirm from '../../../src/components/InlineConfirm';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

interface Hours {
  [key: string]: { open: string; close: string; closed?: boolean };
}

/** EditLocationScreen - edit location details, hours, or delete location. */
export default function EditLocationScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { locationId } = useLocalSearchParams<{ locationId: string }>();
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [stateTaxRate, setStateTaxRate] = useState('');
  const [localTaxRate, setLocalTaxRate] = useState('');
  const [taxJurisdiction, setTaxJurisdiction] = useState('');
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [oosMessage, setOosMessage] = useState(''); // 1.4 P19
  const [promptStats, setPromptStats] = useState<{ customersPrompted: number; tapped: number } | null>(null);
  const [resolvedLat, setResolvedLat] = useState<number | null>(null);
  const [resolvedLng, setResolvedLng] = useState<number | null>(null);
  // Visit Billing (visit tabs) — owner-only feature; customers never see tabs.
  const [tabMode, setTabMode] = useState<'off' | 'charge_at_close' | 'preauth' | 'first_item_then_tab'>('off');
  const [tabInactivity, setTabInactivity] = useState('45');
  const [tabThreshold, setTabThreshold] = useState('');
  const [tabPreauth, setTabPreauth] = useState('');
  const [tabLoaded, setTabLoaded] = useState(false);
  const [hours, setHours] = useState<Hours>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const router = useRouter();
  const toast = useToast();

  useEffect(() => {
    const ac = new AbortController();
    const fetch_ = async () => {
      try {
        const loc = await api<any>(`/api/locations/${locationId}`);
        if (ac.signal.aborted) return;
        setName(loc.name);
        setCompanyName(loc.companyName || '');
        setAddress(loc.address);
        setCity(loc.city);
        setState(loc.state);
        setZip(loc.zip);
        setPhone(loc.phone || '');
        setDescription(loc.description || '');
        setSupportEmail(loc.supportEmail || '');
        // Tax rates are stored as decimals (0.04225); display as percentages (4.225)
        setStateTaxRate(loc.stateTaxRate ? (loc.stateTaxRate * 100).toString() : '');
        setLocalTaxRate(loc.localTaxRate ? (loc.localTaxRate * 100).toString() : '');
        setTaxJurisdiction(loc.taxJurisdiction || '');
        setGoogleReviewUrl(loc.googleReviewUrl || '');
        setOosMessage(loc.oosMessage || '');
        setResolvedLat(loc.latitude ?? null);
        setResolvedLng(loc.longitude ?? null);
        if (loc.hours) setHours(loc.hours);
        // Visit Billing settings (silent on failure — section hides until loaded).
        try {
          const ts = await api<{ mode: typeof tabMode; preauthCents: number | null; inactivityMinutes: number; thresholdCents: number | null }>(
            `/api/owner/tab-settings?locationId=${locationId}`);
          if (ac.signal.aborted) return;
          setTabMode(ts.mode);
          setTabInactivity(String(ts.inactivityMinutes ?? 45));
          setTabThreshold(ts.thresholdCents ? (ts.thresholdCents / 100).toFixed(2) : '');
          setTabPreauth(ts.preauthCents ? (ts.preauthCents / 100).toFixed(2) : '');
          setTabLoaded(true);
        } catch { /* section stays hidden */ }
        // Best-effort prompt stats for the Google Reviews section (silent on failure).
        try {
          const stats = await api<{ rows: { locationId: string; customersPrompted: number; tapped: number }[] }>('/api/owner/review-prompts/stats');
          if (ac.signal.aborted) return;
          const row = stats.rows.find(r => r.locationId === locationId);
          if (row) setPromptStats({ customersPrompted: row.customersPrompted, tapped: row.tapped });
        } catch { /* stats are optional */ }
      } catch (err: any) {
        if (ac.signal.aborted) return;
        toast.show({ type: 'error', title: 'Error', message: err.message });
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };
    fetch_();
    return () => ac.abort();
  }, [locationId]);

  const updateHour = (day: string, field: 'open' | 'close', value: string) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], open: prev[day]?.open || '06:00', close: prev[day]?.close || '22:00', [field]: value },
    }));
  };

  const toggleDay = (day: string) => {
    setHours(prev => {
      if (prev[day]) {
        const { [day]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [day]: { open: '06:00', close: '22:00' } };
    });
  };

  const handleSave = async () => {
    if (!name || !address || !city || !state || !zip) {
      toast.show({ type: 'error', title: 'Please fill in all required fields' });
      return;
    }
    const reviewUrl = googleReviewUrl.trim();
    if (reviewUrl && !/^https?:\/\//i.test(reviewUrl)) {
      toast.show({ type: 'error', title: 'Google review link must start with https://', message: 'Copy the full link from your Google Business Profile.' });
      return;
    }
    setSaving(true);
    try {
      const parsePct = (v: string): number | undefined => {
        const trimmed = v.trim();
        if (!trimmed) return undefined;
        const n = Number(trimmed);
        if (isNaN(n) || n < 0 || n > 100) return undefined;
        return n / 100;
      };
      await api(`/api/locations/${locationId}`, {
        method: 'PUT',
        body: {
          name, address, city, state, zip,
          companyName: companyName || undefined,
          phone: phone || undefined,
          supportEmail: supportEmail || undefined,
          description: description || undefined,
          hours: Object.keys(hours).length > 0 ? hours : undefined,
          stateTaxRate: parsePct(stateTaxRate),
          localTaxRate: parsePct(localTaxRate),
          taxJurisdiction: taxJurisdiction || undefined,
          googleReviewUrl: reviewUrl,   // '' clears the link server-side
          oosMessage: oosMessage.trim() || null,
        },
      });
      // Visit Billing settings ride their own endpoint.
      if (tabLoaded) {
        const dollars = (v: string): number | null => {
          const n = Number(v.trim());
          return v.trim() && !isNaN(n) && n > 0 ? Math.round(n * 100) : null;
        };
        if (tabMode === 'preauth' && !dollars(tabPreauth)) {
          toast.show({ type: 'error', title: 'Pre-authorization amount is required for pre-auth mode' });
          setSaving(false);
          return;
        }
        const inactivity = Number(tabInactivity.trim());
        await api('/api/owner/tab-settings', {
          method: 'PATCH',
          body: {
            locationId,
            mode: tabMode,
            preauthCents: tabMode === 'preauth' ? dollars(tabPreauth) : null,
            inactivityMinutes: !isNaN(inactivity) && inactivity >= 5 && inactivity <= 720 ? inactivity : null,
            thresholdCents: dollars(tabThreshold),
          },
        });
      }
      toast.show({ type: 'success', title: 'Location updated' });
      router.replace(`/owner/location/${locationId}` as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await api(`/api/locations/${locationId}`, { method: 'DELETE' });
      router.replace('/owner' as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) return <View style={styles.container}><Text style={styles.loading}>Loading...</Text></View>;

  return (
    <>
      <Stack.Screen options={{ title: 'Edit Location' }} />
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
          <ContentContainer>
          <Text style={styles.section}>Details</Text>
          <Input label="Location Name *" value={name} onChangeText={setName} icon="business-outline" />
          <Input label="Company Name" value={companyName} onChangeText={setCompanyName} icon="briefcase-outline" placeholder="Sparkle Wash LLC" />
          <Input label="Street Address *" value={address} onChangeText={setAddress} icon="location-outline" />
          <Input label="City *" value={city} onChangeText={setCity} />
          <View style={styles.row}>
            <View style={styles.flex1}><Input label="State *" value={state} onChangeText={setState} maxLength={2} autoCapitalize="characters" /></View>
            <View style={styles.flex1}><Input label="ZIP *" value={zip} onChangeText={setZip} keyboardType="number-pad" /></View>
          </View>
          <Input label="Phone" value={phone} onChangeText={setPhone} icon="call-outline" keyboardType="phone-pad" />
          <Input label="Support Email" value={supportEmail} onChangeText={setSupportEmail} icon="mail-outline" keyboardType="email-address" autoCapitalize="none" placeholder="reports@yourbusiness.com" />
          <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            Customer problem reports sent here. Falls back to your account email if blank.
          </Text>
          <Input label="Description" value={description} onChangeText={setDescription} icon="document-text-outline" multiline numberOfLines={3} maxLength={500} />

          <Text style={styles.section}>Sales Tax</Text>
          <Text style={{ fontSize: 12, color: colors.textSecondary, marginBottom: spacing.sm }}>
            Configure if you collect sales tax on washes. Used by the Sales Tax Report on your dashboard. Posted prices are assumed tax-inclusive (tax is backed out of gross). Leave at 0 if you don&apos;t collect tax.
          </Text>
          <View style={styles.row}>
            <View style={styles.flex1}><Input label="State rate (%)" value={stateTaxRate} onChangeText={setStateTaxRate} keyboardType="decimal-pad" placeholder="4.225" /></View>
            <View style={styles.flex1}><Input label="Local rate (%)" value={localTaxRate} onChangeText={setLocalTaxRate} keyboardType="decimal-pad" placeholder="4.5" /></View>
          </View>
          <Input label="Tax Jurisdiction" value={taxJurisdiction} onChangeText={setTaxJurisdiction} icon="location-outline" placeholder="St. Francois County, Bonne Terre" maxLength={200} />

          <Text style={styles.section}>Google Reviews</Text>
          <Text style={{ fontSize: 12, color: colors.textSecondary, marginBottom: spacing.sm }}>
            Add your Google review link and customers who rate a wash 5 stars get a gentle, one-time ask to leave a Google review (repeated only after 60 days if they didn&apos;t tap through). To get the link: sign in at business.google.com, pick this business, tap &quot;Ask for reviews&quot;, and copy the share link — it looks like g.page/r/XXXX/review. Leave blank to turn the prompt off.
          </Text>
          <Input label="Google Review Link" value={googleReviewUrl} onChangeText={setGoogleReviewUrl} icon="logo-google" autoCapitalize="none" keyboardType="url" placeholder="https://g.page/r/XXXX/review" maxLength={500} />
          <Input label="Out-of-service message (all bays here)" value={oosMessage} onChangeText={setOosMessage} icon="warning-outline" placeholder="This bay is temporarily out of service." maxLength={200} />
          {promptStats && (
            <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
              Shown to {promptStats.customersPrompted} customer{promptStats.customersPrompted === 1 ? '' : 's'} · {promptStats.tapped} tapped through to Google.
            </Text>
          )}

          {resolvedLat != null && resolvedLng != null && (
            <Text style={{ fontSize: 12, color: colors.textSecondary, marginBottom: spacing.sm }}>
              Location coordinates resolved from your address: {resolvedLat.toFixed(4)}, {resolvedLng.toFixed(4)} — used to fetch weather on your dashboard.
            </Text>
          )}

          {tabLoaded && (
            <>
              <Text style={styles.section}>Visit Billing</Text>
              <Text style={{ fontSize: 12, color: colors.textSecondary, marginBottom: spacing.sm }}>
                Combine a customer&apos;s same-visit purchases (washes, vacuums, vending) into one card charge — saves the $0.30 per-transaction fee on every extra purchase. Customers see no change: they pay per selection as usual and get one combined charge with an itemized receipt email.
              </Text>
              {([
                { value: 'off', label: 'Off — charge each purchase separately', icon: 'card-outline' },
                { value: 'charge_at_close', label: 'Charge once at the end of the visit', icon: 'time-outline' },
                { value: 'preauth', label: 'Pre-authorize at the start, capture at the end', icon: 'shield-checkmark-outline' },
                {
                  value: 'first_item_then_tab',
                  label: 'Charge the wash, combine the rest',
                  sub: 'Self-serve and automatic washes charge normally; vacuums and vending combine into one end-of-visit charge.',
                  icon: 'layers-outline',
                },
              ] as const).map(opt => (
                <TouchableOpacity key={opt.value} style={styles.quickOption} onPress={() => setTabMode(opt.value)}>
                  <Ionicons name={tabMode === opt.value ? 'radio-button-on' : 'radio-button-off'} size={18} color={colors.primary} />
                  <Ionicons name={opt.icon} size={16} color={colors.textSecondary} />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.quickOptionText}>{opt.label}</Text>
                    {'sub' in opt && (
                      <Text style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>{opt.sub}</Text>
                    )}
                  </View>
                </TouchableOpacity>
              ))}
              {tabMode !== 'off' && (
                <>
                  <View style={styles.row}>
                    <View style={styles.flex1}>
                      <Input label="Close after idle (min)" value={tabInactivity} onChangeText={setTabInactivity} keyboardType="number-pad" placeholder="45" />
                    </View>
                    <View style={styles.flex1}>
                      <Input label="Auto-charge at ($)" value={tabThreshold} onChangeText={setTabThreshold} keyboardType="decimal-pad" placeholder="none" />
                    </View>
                  </View>
                  {tabMode === 'preauth' && (
                    <Input label="Pre-authorization amount ($) *" value={tabPreauth} onChangeText={setTabPreauth} keyboardType="decimal-pad" placeholder="30.00" />
                  )}
                  <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
                    The visit closes after the idle window (or at the auto-charge amount) and the card is charged once. Failed charges retry automatically and pause new purchases for that customer until collected.
                  </Text>
                </>
              )}
            </>
          )}

          <Text style={styles.section}>Operating Hours</Text>
          <TouchableOpacity style={styles.quickOption} onPress={() => {
            const allDays: Hours = {};
            DAYS.forEach(d => { allDays[d] = { open: '00:00', close: '23:59' }; });
            setHours(allDays);
          }}>
            <Ionicons name="time" size={18} color={colors.primary} />
            <Text style={styles.quickOptionText}>Set 24/7</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.quickOption} onPress={() => {
            const typical: Hours = {};
            DAYS.forEach(d => { typical[d] = { open: '06:00', close: '22:00' }; });
            setHours(typical);
          }}>
            <Ionicons name="sunny" size={18} color={colors.primary} />
            <Text style={styles.quickOptionText}>Typical Hours</Text>
          </TouchableOpacity>
          {DAYS.map(day => (
            <View key={day} style={styles.hoursRow}>
              <TouchableOpacity style={styles.dayToggle} onPress={() => toggleDay(day)}>
                <View style={[styles.checkbox, hours[day] && styles.checkboxActive]}>
                  {hours[day] && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.dayLabel}>{day.slice(0, 3).charAt(0).toUpperCase() + day.slice(1, 3)}</Text>
              </TouchableOpacity>
              {hours[day] ? (
                <View style={styles.hoursInputRow}>
                  <TextInput placeholder="06:00" placeholderTextColor={colors.textLight} value={hours[day]?.open || ''} onChangeText={v => updateHour(day, 'open', v)} style={styles.hoursInput} maxLength={5} />
                  <Text style={styles.hoursDash}>–</Text>
                  <TextInput placeholder="22:00" placeholderTextColor={colors.textLight} value={hours[day]?.close || ''} onChangeText={v => updateHour(day, 'close', v)} style={styles.hoursInput} maxLength={5} />
                </View>
              ) : (
                <Text style={styles.closedText}>Closed</Text>
              )}
            </View>
          ))}

          <Button title="Save Changes" onPress={handleSave} loading={saving} size="lg" style={styles.saveBtn} />
          <Button title="Cancel" onPress={() => router.back()} variant="outline" size="md" style={styles.cancelBtn} />
          <Button title="Delete Location" onPress={() => setShowDeleteConfirm(true)} variant="danger" size="md" style={styles.deleteBtn} />
          <InlineConfirm
            message="This will permanently delete this location and all its bays. Are you sure?"
            visible={showDeleteConfirm}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteConfirm(false)}
            confirmLabel="Delete Location"
            loading={deleteLoading}
            variant="danger"
          />
          </ContentContainer>
        </ScrollView>
      </KeyboardAvoidingView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { padding: spacing.lg },
  loading: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xxl },
  section: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md, marginTop: spacing.md },
  row: { flexDirection: 'row', gap: spacing.sm },
  flex1: { flex: 1 },
  hoursRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm, marginBottom: 6, minHeight: 44 },
  dayToggle: { flexDirection: 'row', alignItems: 'center', gap: 6, width: 64, flexShrink: 0 },
  checkbox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: c.border, justifyContent: 'center', alignItems: 'center' },
  checkboxActive: { backgroundColor: c.primary, borderColor: c.primary },
  checkmark: { color: c.white, fontSize: 12, fontWeight: fontWeight.bold },
  dayLabel: { fontSize: fontSize.xs, color: c.text, fontWeight: fontWeight.medium },
  hoursInputRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1, minWidth: 170, maxWidth: '100%' },
  hoursInput: { flex: 1, minWidth: 0, fontSize: fontSize.sm, textAlign: 'center' as const, color: c.text, backgroundColor: c.surface, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, paddingVertical: Platform.OS === 'android' ? 10 : 8, paddingHorizontal: 4, minHeight: 40 },
  hoursDash: { color: c.textLight, fontSize: fontSize.xs },
  closedText: { fontSize: fontSize.xs, color: c.textLight, fontStyle: 'italic' },
  quickOption: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, marginBottom: spacing.xs },
  quickOptionText: { fontSize: fontSize.sm, color: c.primaryDark, fontWeight: fontWeight.medium },
  saveBtn: { marginTop: spacing.lg },
  cancelBtn: { marginTop: spacing.sm },
  deleteBtn: { marginTop: spacing.md, marginBottom: spacing.xxl },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
